/**
 * The owner's START. Pressing it records a request; the execution side checks
 * the account against fresh exchange facts within a minute and either starts
 * it or says why not. The page never decides readiness itself.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ACCOUNT_REFRESH_MS } from "@/shared/clients/refresh";
import { useLegalDocuments } from "@/shared/legal/legal";

import { Button } from "@/design-system/Button";
import type { BadgeTone } from "@/design-system/StatusBadge";
import { readTradingControl, requestTradingStart } from "@/shared/api";
import type { TradingControlResponse } from "@/shared/api";

const REASON: Readonly<Record<string, string>> = {
  ALREADY_STARTED: "торговля уже запущена",
  NO_STRATEGY_CONTRACT: "на счёте не установлена стратегия",
  NO_ACTIVE_STRATEGY: "назначьте стратегию и включите для неё новые циклы",
  ACCOUNT_NOT_FLAT_OR_UNRECONCILED: "на счёте есть позиции или ордера вне стратегии, либо сверка с биржей не завершена",
  WAITING_FOR_ADMISSIBLE_CANDIDATE: "ждём первую монету, прошедшую фильтр входа",
  PAUSED_BY_OWNER: "отменено паузой",
};

/** A reason the vocabulary does not know is shown as itself, never hidden. */
function reason(code: string | null | undefined): string {
  if (code === null || code === undefined) return "";
  return REASON[code] ?? code;
}

export const tradingControlKey = (tenantId: string, accountId: string) =>
  ["trading-control", tenantId, accountId] as const;

export function useTradingControl(tenantId: string, accountId: string) {
  return useQuery({
    queryKey: tradingControlKey(tenantId, accountId),
    retry: false,
    queryFn: async (): Promise<TradingControlResponse> => {
      const { data } = await readTradingControl({ credentials: "same-origin", cache: "no-store" });
      if (data === undefined) throw new Error("trading control unavailable");
      return data;
    },
    // A request is decided within a supervision pass: poll fast while it
    // waits, and keep up otherwise — a pause from elsewhere must show.
    refetchInterval: (query) => query.state.data?.start_request?.state === "PENDING" ? 5000 : ACCOUNT_REFRESH_MS,
  });
}

export function startStep(control: TradingControlResponse | undefined, failed: boolean): {
  readonly label: string; readonly state: string; readonly tone: BadgeTone;
} {
  if (control === undefined) return { label: "Запуск", state: failed ? "состояние неизвестно" : "проверяю…", tone: "muted" };
  if (control.admissions_enabled) return { label: "Запуск", state: "торговля запущена", tone: "ready" };
  const request = control.start_request;
  if (request?.state === "PENDING") {
    return { label: "Запуск", state: request.reason === null ? "проверяем счёт и запускаем…" : reason(request.reason), tone: "warning" };
  }
  if (request?.state === "REFUSED") return { label: "Запуск", state: `не запущено: ${reason(request.reason)}`, tone: "warning" };
  return { label: "Запуск", state: "ждёт вашей команды", tone: "muted" };
}

export function StartControl({ tenantId, accountId, control, onChanged }: {
  readonly tenantId: string; readonly accountId: string;
  readonly control: TradingControlResponse; readonly onChanged: () => Promise<void>;
}): React.JSX.Element | null {
  const queries = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [riskAccepted, setRiskAccepted] = useState(false);
  const legal = useLegalDocuments(confirming);
  const risk = legal.data?.documents.find((document) => document.kind === "RISK");
  const start = useMutation({
    mutationFn: async (password: string | undefined) => {
      const { error, response } = await requestTradingStart({
        credentials: "same-origin",
        cache: "no-store",
        // Within two hours of signing in the sign-in is the confirmation.
        body: {
          request_id: crypto.randomUUID(),
          risk_version: risk?.version ?? null,
          ...(password === undefined ? {} : { password }),
        },
      });
      if (error !== undefined || response?.ok !== true) {
        const failure: unknown = error;
        const detail = typeof failure === "object" && failure !== null && "detail" in failure && typeof failure.detail === "string" ? failure.detail : "";
        throw new Error(detail);
      }
    },
    onSuccess: async () => {
      setConfirming(false);
      await queries.invalidateQueries({ queryKey: tradingControlKey(tenantId, accountId) });
      await onChanged();
    },
  });
  if (!control.can_manage || control.admissions_enabled || control.start_request?.state === "PENDING") return null;
  return (
    <div className="flex flex-col gap-2" data-testid="start-control">
      {!confirming ? (
        <div>
          <Button variant="primary" onClick={() => { setConfirming(true); start.reset(); }}>Запустить торговлю</Button>
        </div>
      ) : (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            const field = new FormData(event.currentTarget).get("password");
            event.currentTarget.reset();
            if (!start.isPending && riskAccepted) start.mutate(typeof field === "string" && field !== "" ? field : undefined);
          }}
        >
          <p className="text-helper-sm">
            Платформа проверит ключ, свежесть данных и сверку с биржей, затем начнёт открывать циклы по схеме слотов.
            Позиции на счёте вне стратегии запуск остановят.
          </p>
          <div className="max-h-48 overflow-y-auto whitespace-pre-line rounded-md border border-status-warning p-3 text-helper-sm text-text-secondary" data-testid="start-risk" tabIndex={0}>
            <p className="mb-1 font-semibold text-text-primary">{risk?.title ?? "Уведомление о рисках"}</p>
            {risk?.body ?? "Загружаю…"}
          </div>
          <label className="flex cursor-pointer items-start gap-2 text-text-primary">
            <input type="checkbox" className="mt-1 size-4 cursor-pointer" checked={riskAccepted} onChange={(event) => { setRiskAccepted(event.target.checked); }} />
            <span>Я понимаю риски, принимаю решение о запуске самостоятельно и несу ответственность за его последствия.</span>
          </label>
          {control.confirmation_required === false ? (
            <p className="text-helper-sm text-text-muted" data-testid="start-recent-sign-in">Вы недавно вошли — пароль вводить не нужно.</p>
          ) : (
            <label className="flex flex-col gap-1">
              <span>Пароль Skryptos</span>
              <input name="password" type="password" required maxLength={1024} autoComplete="current-password"
                className="min-h-9 rounded-row border border-border-strong bg-surface-default px-2 text-text-primary focus-visible:outline-2 focus-visible:outline-focus-ring" />
            </label>
          )}
          {control.confirmation_required !== false && (
            <p className="text-helper-sm text-text-muted">Тот, с которым вы входите на сайт. Или выйдите и войдите снова — тогда пароль не понадобится.</p>
          )}
          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={start.isPending || !riskAccepted || risk === undefined}>Подтвердить запуск</Button>
            <Button type="button" onClick={() => { setConfirming(false); }}>Отмена</Button>
          </div>
        </form>
      )}
      {start.isError && (
        <p role="alert" className="text-status-warning">
          {start.error.message === "TERMS_NOT_ACCEPTED" ? "Сначала примите условия использования — обновите страницу." : start.error.message === "RISK_NOT_ACKNOWLEDGED" ? "Уведомление о рисках обновилось — прочитайте его и подтвердите снова." : start.error.message === "REAUTHENTICATION_REQUIRED" ? "Пароль Skryptos не подошёл. Выйдите и войдите снова — после входа пароль здесь не нужен." : "Запуск не принят. Обновите страницу и повторите."}
        </p>
      )}
    </div>
  );
}
